/**
 * Alshallal Pastry Company - Pending Employee Updates Page
 * Phase D2: Document-Driven Employee Auto-Updates
 * 
 * HR Manager dashboard for reviewing and approving high-risk (Mode B) 
 * pending employee profile updates triggered by document issuance.
 * 
 * Access: HR_MANAGER + SUPER_ADMIN only
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  ShieldAlert,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  FileText,
  User,
  Calendar,
  Filter,
  ArrowRight,
  AlertTriangle,
  Briefcase,
  DollarSign,
  AlertOctagon,
  Search,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useListViewState, useDebouncedUrlText } from '../../hooks/useListViewState';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock, label_en: 'Pending', label_ar: 'في الانتظار' },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label_en: 'Approved', label_ar: 'تمت الموافقة' },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, label_en: 'Rejected', label_ar: 'مرفوض' },
  approved_scheduled: { bg: 'bg-blue-100 text-blue-800', icon: Calendar, label_en: 'Approved (Scheduled)', label_ar: 'تمت الموافقة (مجدول)' },
};

// Document type labels (from catalog)
const DOCUMENT_TYPE_LABELS = {
  'PROMOTION_LETTER': { en: 'Promotion Letter', ar: 'خطاب ترقية' },
  'SALARY_INCREMENT_LETTER': { en: 'Salary Increment Letter', ar: 'خطاب زيادة راتب' },
  'TRANSFER_LETTER': { en: 'Transfer Letter', ar: 'خطاب نقل' },
  'WARNING_LETTER': { en: 'Warning Letter', ar: 'خطاب إنذار' },
  'SALARY_DECREASE_LETTER': { en: 'Salary Decrease Letter', ar: 'خطاب تخفيض راتب' },
  'TERMINATION_LETTER': { en: 'Termination Letter', ar: 'خطاب إنهاء خدمة' },
  'BANK_DETAILS_UPDATE': { en: 'Bank Details Update', ar: 'تحديث البيانات البنكية' },
};

const PendingEmployeeUpdatesPage = () => {
  const { token, hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();

  // State
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination and filters live in the URL, so opening an employee from here
  // and coming back returns to the same page with the same filters.
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [view, setView] = useListViewState('pending-updates', {
    page: 1,
    status: 'pending',
    employee_search: '',
    document_type: '',
  });
  const { page } = view;
  const filters = useMemo(() => ({
    status: view.status,
    employee_search: view.employee_search,
    document_type: view.document_type,
  }), [view.status, view.employee_search, view.document_type]);

  const setPage = useCallback((next) => setView({ page: typeof next === 'function' ? next(page) : next }), [setView, page]);
  const setFilters = useCallback((update) => {
    const next = typeof update === 'function' ? update(filters) : update;
    setView({ ...next, page: 1 });
  }, [setView, filters]);

  const commitEmployeeSearch = useCallback((value) => setView({ employee_search: value, page: 1 }), [setView]);
  const [employeeSearchInput, setEmployeeSearchInput] = useDebouncedUrlText(view.employee_search, commitEmployeeSearch);

  // Review dialog
  const [reviewDialog, setReviewDialog] = useState({ 
    open: false, 
    update: null, 
    action: null 
  });
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Detail view
  const [expandedRow, setExpandedRow] = useState(null);

  // RBAC check
  const canManage = hasRole('hr_manager', 'super_admin');

  // Fetch pending updates
  const fetchUpdates = useCallback(async () => {
    if (!canManage) return;
    
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }

      const response = await axios.get(`${API_URL}/pending-updates?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUpdates(response.data.updates || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch pending updates:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load pending updates');
    } finally {
      setLoading(false);
    }
  }, [token, canManage, page, pageSize, filters.status]);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  // Handle approve
  const handleApprove = async () => {
    if (!reviewDialog.update) return;

    try {
      setSubmitting(true);
      await axios.post(
        `${API_URL}/pending-updates/${reviewDialog.update.id}/approve`,
        { review_notes: reviewNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(isRTL ? 'تمت الموافقة على التحديث بنجاح' : 'Update approved successfully');
      setReviewDialog({ open: false, update: null, action: null });
      setReviewNotes('');
      fetchUpdates();
    } catch (err) {
      console.error('Approval failed:', err);
      toast.error(err.response?.data?.detail?.error || 'Failed to approve update');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!reviewDialog.update || reviewNotes.length < 1) {
      toast.error(isRTL ? 'سبب الرفض مطلوب' : 'Rejection reason is required');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${API_URL}/pending-updates/${reviewDialog.update.id}/reject`,
        { review_notes: reviewNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(isRTL ? 'تم رفض التحديث' : 'Update rejected');
      setReviewDialog({ open: false, update: null, action: null });
      setReviewNotes('');
      fetchUpdates();
    } catch (err) {
      console.error('Rejection failed:', err);
      toast.error(err.response?.data?.detail?.error || 'Failed to reject update');
    } finally {
      setSubmitting(false);
    }
  };

  // Group changes by domain
  const groupChangesByDomain = (changes) => {
    if (!changes || changes.length === 0) return {};
    
    const groups = {
      employment: { 
        label_en: 'Employment', 
        label_ar: 'بيانات التوظيف',
        icon: Briefcase,
        fields: ['job_title', 'job_title_ar', 'department', 'department_ar', 'department_id', 'branch_id', 'manager_id', 'status', 'contract_end_date', 'termination_date'],
        changes: []
      },
      compensation: { 
        label_en: 'Compensation', 
        label_ar: 'التعويضات',
        icon: DollarSign,
        fields: ['salary', 'housing_allowance', 'transport_allowance', 'other_allowances'],
        changes: []
      },
      disciplinary: { 
        label_en: 'Disciplinary', 
        label_ar: 'تأديبي',
        icon: AlertOctagon,
        fields: [],
        changes: []
      },
      banking: { 
        label_en: 'Banking', 
        label_ar: 'البيانات البنكية',
        icon: ShieldAlert,
        fields: ['bank_name', 'iban'],
        changes: []
      }
    };
    
    changes.forEach(change => {
      const field = change.field;
      let assigned = false;
      
      if (change.is_deduction) {
        groups.disciplinary.changes.push(change);
        assigned = true;
      }
      
      if (!assigned) {
        for (const [, group] of Object.entries(groups)) {
          if (group.fields.includes(field)) {
            group.changes.push(change);
            assigned = true;
            break;
          }
        }
      }
      
      if (!assigned) {
        groups.employment.changes.push(change);
      }
    });
    
    return Object.fromEntries(
      Object.entries(groups).filter(([, group]) => group.changes.length > 0)
    );
  };

  // Format value for display
  const formatChangeValue = (value, field) => {
    if (value === null || value === undefined || value === '') {
      return isRTL ? '(فارغ)' : '(empty)';
    }
    if (['salary', 'housing_allowance', 'transport_allowance', 'other_allowances'].includes(field)) {
      return `${Number(value).toLocaleString()} SAR`;
    }
    if (['contract_end_date', 'termination_date', 'effective_date'].includes(field)) {
      try {
        return format(new Date(value), 'PP');
      } catch {
        return value;
      }
    }
    return String(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  const getDocTypeLabel = (code) => {
    const labels = DOCUMENT_TYPE_LABELS[code];
    if (labels) {
      return isRTL ? labels.ar : labels.en;
    }
    return code;
  };

  // RBAC guard
  if (!canManage) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="p-8 text-center">
          <ShieldAlert className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            {isRTL ? 'الوصول مرفوض' : 'Access Denied'}
          </h2>
          <p className="text-slate-600">
            {isRTL 
              ? 'هذه الصفحة متاحة فقط لمدير الموارد البشرية والمشرف العام' 
              : 'This page is only accessible to HR Manager and Super Admin'}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="pending-updates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-amber-600" />
            {isRTL ? 'تحديثات الموظفين المعلقة' : 'Pending Employee Updates'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isRTL 
              ? 'مراجعة والموافقة على تحديثات ملفات الموظفين الناتجة عن إصدار المستندات' 
              : 'Review and approve employee profile updates triggered by document issuance'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchUpdates} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-2xl font-bold text-slate-900">{total}</div>
          <div className="text-sm text-slate-600">{isRTL ? 'إجمالي التحديثات' : 'Total Updates'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-amber-600">
            {updates.filter(u => u.status === 'pending').length}
          </div>
          <div className="text-sm text-slate-600">{isRTL ? 'في انتظار المراجعة' : 'Pending Review'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600">
            {updates.filter(u => u.status === 'approved' || u.status === 'approved_scheduled').length}
          </div>
          <div className="text-sm text-slate-600">{isRTL ? 'تمت الموافقة' : 'Approved'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-red-600">
            {updates.filter(u => u.status === 'rejected').length}
          </div>
          <div className="text-sm text-slate-600">{isRTL ? 'مرفوض' : 'Rejected'}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {isRTL ? 'تصفية' : 'Filters'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div className="space-y-2">
              <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
              <Select 
                value={filters.status} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{isRTL ? 'في الانتظار' : 'Pending'}</SelectItem>
                  <SelectItem value="approved">{isRTL ? 'تمت الموافقة' : 'Approved'}</SelectItem>
                  <SelectItem value="rejected">{isRTL ? 'مرفوض' : 'Rejected'}</SelectItem>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Employee Search */}
            <div className="space-y-2">
              <Label>{isRTL ? 'بحث عن موظف' : 'Search Employee'}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={employeeSearchInput}
                  onChange={(e) => setEmployeeSearchInput(e.target.value)}
                  placeholder={isRTL ? 'اسم أو رقم الموظف...' : 'Name or employee number...'}
                  className="pl-9"
                  data-testid="filter-employee-search"
                />
              </div>
            </div>

            {/* Document Type Filter */}
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع المستند' : 'Document Type'}</Label>
              <Select 
                value={filters.document_type} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, document_type: v }))}
              >
                <SelectTrigger data-testid="filter-doc-type">
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All Types'}</SelectItem>
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([code, labels]) => (
                    <SelectItem key={code} value={code}>
                      {isRTL ? labels.ar : labels.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">
            ×
          </button>
        </div>
      )}

      {/* Updates Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : updates.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد تحديثات معلقة' : 'No Pending Updates'}
              </h3>
              <p className="text-slate-600">
                {isRTL 
                  ? 'جميع تحديثات الموظفين تمت معالجتها' 
                  : 'All employee updates have been processed'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                  <TableHead>{isRTL ? 'نوع المستند' : 'Document Type'}</TableHead>
                  <TableHead>{isRTL ? 'التغييرات' : 'Changes'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ السريان' : 'Effective Date'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead>{isRTL ? 'تم الإنشاء' : 'Created'}</TableHead>
                  <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {updates
                  .filter(u => {
                    // Apply client-side filters
                    if (filters.employee_search) {
                      const search = filters.employee_search.toLowerCase();
                      const nameMatch = (u.employee_name_en || '').toLowerCase().includes(search) ||
                                       (u.employee_name_ar || '').toLowerCase().includes(search) ||
                                       (u.employee_number || '').toLowerCase().includes(search);
                      if (!nameMatch) return false;
                    }
                    if (filters.document_type && filters.document_type !== 'all') {
                      if (u.document_type_code !== filters.document_type) return false;
                    }
                    return true;
                  })
                  .map((update) => {
                    const StatusIcon = STATUS_STYLES[update.status]?.icon || Clock;
                    const statusStyle = STATUS_STYLES[update.status] || STATUS_STYLES.pending;
                    const isExpanded = expandedRow === update.id;

                    return (
                      <React.Fragment key={update.id}>
                        <TableRow 
                          className={cn("cursor-pointer hover:bg-slate-50", isExpanded && "bg-slate-50")}
                          onClick={() => setExpandedRow(isExpanded ? null : update.id)}
                        >
                          <TableCell>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-slate-400" />
                              <div>
                                <div className="font-medium">
                                  {isRTL ? update.employee_name_ar : update.employee_name_en}
                                </div>
                                <div className="text-xs text-slate-500">{update.employee_number}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <span>{getDocTypeLabel(update.document_type_code)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {update.changes?.length || 0} {isRTL ? 'تغيير' : 'change(s)'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {update.effective_date ? (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4 text-slate-400" />
                                <span>{formatDate(update.effective_date)}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusStyle.bg}>
                              <StatusIcon className="h-3 w-3 me-1" />
                              {isRTL ? statusStyle.label_ar : statusStyle.label_en}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {formatDate(update.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              {update.status === 'pending' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => setReviewDialog({ open: true, update, action: 'approve' })}
                                    title={isRTL ? 'موافقة' : 'Approve'}
                                    data-testid={`approve-btn-${update.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => setReviewDialog({ open: true, update, action: 'reject' })}
                                    title={isRTL ? 'رفض' : 'Reject'}
                                    data-testid={`reject-btn-${update.id}`}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/employees/${update.employee_id}`)}
                                title={isRTL ? 'عرض الموظف' : 'View Employee'}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Expanded row - Show change details */}
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={8} className="bg-slate-50 p-4">
                              <div className="space-y-4">
                                {/* High Risk Reason */}
                                {update.high_risk_reason && (
                                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                    <div>
                                      <div className="font-medium text-amber-800">
                                        {isRTL ? 'سبب المخاطرة العالية' : 'High Risk Reason'}
                                      </div>
                                      <div className="text-sm text-amber-700">{update.high_risk_reason}</div>
                                    </div>
                                  </div>
                                )}

                                {/* Changes grouped by domain */}
                                <div className="border rounded-lg divide-y bg-white">
                                  {Object.entries(groupChangesByDomain(update.changes || [])).map(([groupKey, group]) => {
                                    const GroupIcon = group.icon;
                                    return (
                                      <div key={groupKey} className="p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                          <GroupIcon className="h-4 w-4 text-slate-500" />
                                          <h5 className="font-medium text-slate-700">
                                            {isRTL ? group.label_ar : group.label_en}
                                          </h5>
                                        </div>
                                        <div className="space-y-2">
                                          {group.changes.map((change, idx) => (
                                            <div 
                                              key={`${change.field}-${idx}`}
                                              className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-sm"
                                            >
                                              <span className="font-medium text-slate-600">
                                                {isRTL ? change.label_ar : change.label_en}
                                              </span>
                                              <div className="flex items-center gap-2">
                                                <span className="text-slate-500 line-through">
                                                  {formatChangeValue(change.old_value, change.field)}
                                                </span>
                                                <ArrowRight className="h-4 w-4 text-slate-400" />
                                                <span className={cn(
                                                  "font-semibold",
                                                  change.is_deduction ? "text-red-600" : "text-emerald-600"
                                                )}>
                                                  {formatChangeValue(change.new_value, change.field)}
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Issuer info */}
                                <div className="text-sm text-slate-500">
                                  {isRTL ? 'تم الإنشاء بواسطة:' : 'Created by:'}{' '}
                                  <span className="font-medium">{update.created_by_email}</span>
                                  {' '}{isRTL ? 'في' : 'on'}{' '}
                                  <span className="font-medium">{formatDate(update.created_at)}</span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-slate-600">
                {isRTL
                  ? `${total} تحديث`
                  : `${total} updates`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {isRTL ? 'السابق' : 'Previous'}
                </Button>
                <span className="text-sm text-slate-600">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {isRTL ? 'التالي' : 'Next'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog 
        open={reviewDialog.open} 
        onOpenChange={(open) => {
          if (!open) {
            setReviewDialog({ open: false, update: null, action: null });
            setReviewNotes('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className={cn(
              "flex items-center gap-2",
              reviewDialog.action === 'approve' ? 'text-green-600' : 'text-red-600'
            )}>
              {reviewDialog.action === 'approve' 
                ? <CheckCircle className="h-5 w-5" />
                : <XCircle className="h-5 w-5" />
              }
              {reviewDialog.action === 'approve'
                ? (isRTL ? 'الموافقة على التحديث' : 'Approve Update')
                : (isRTL ? 'رفض التحديث' : 'Reject Update')}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.update && (
                <span>
                  {isRTL ? 'موظف:' : 'Employee:'}{' '}
                  <strong>{isRTL ? reviewDialog.update.employee_name_ar : reviewDialog.update.employee_name_en}</strong>
                  {' '}-{' '}
                  {getDocTypeLabel(reviewDialog.update?.document_type_code)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* High risk warning */}
            {reviewDialog.update?.high_risk_reason && reviewDialog.action === 'approve' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-amber-800">
                      {isRTL ? 'تحذير: إجراء عالي المخاطر' : 'Warning: High-Risk Action'}
                    </div>
                    <div className="text-sm text-amber-700">
                      {reviewDialog.update.high_risk_reason}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Changes summary */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-sm font-medium text-slate-700 mb-2">
                {isRTL ? 'التغييرات المطلوبة:' : 'Requested Changes:'}
              </div>
              <div className="space-y-1">
                {(reviewDialog.update?.changes || []).map((change, idx) => (
                  <div key={idx} className="text-sm flex items-center gap-2">
                    <span className="text-slate-600">
                      {isRTL ? change.label_ar : change.label_en}:
                    </span>
                    <span className="text-slate-400 line-through text-xs">
                      {formatChangeValue(change.old_value, change.field)}
                    </span>
                    <ArrowRight className="h-3 w-3 text-slate-400" />
                    <span className={cn(
                      "font-medium",
                      change.is_deduction ? "text-red-600" : "text-emerald-600"
                    )}>
                      {formatChangeValue(change.new_value, change.field)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Review Notes */}
            <div className="space-y-2">
              <Label>
                {reviewDialog.action === 'approve'
                  ? (isRTL ? 'ملاحظات (اختياري)' : 'Notes (optional)')
                  : (isRTL ? 'سبب الرفض (مطلوب)' : 'Rejection Reason (required)')}
              </Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={
                  reviewDialog.action === 'approve'
                    ? (isRTL ? 'أضف ملاحظات...' : 'Add notes...')
                    : (isRTL ? 'أدخل سبب الرفض...' : 'Enter rejection reason...')
                }
                rows={3}
                data-testid="review-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setReviewDialog({ open: false, update: null, action: null });
                setReviewNotes('');
              }}
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={reviewDialog.action === 'approve' ? handleApprove : handleReject}
              disabled={submitting || (reviewDialog.action === 'reject' && reviewNotes.length < 1)}
              className={reviewDialog.action === 'approve' 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-review-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {isRTL ? 'جاري...' : 'Processing...'}</>
              ) : (
                reviewDialog.action === 'approve' 
                  ? (isRTL ? 'تأكيد الموافقة' : 'Confirm Approval') 
                  : (isRTL ? 'تأكيد الرفض' : 'Confirm Rejection')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingEmployeeUpdatesPage;
